import { Box } from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import type { Block } from '@/blocks/domain/types';
import { blockKindDisplayLabel } from '@/blocks/domain/helpers/blockKindDisplayLabel';
import { isDraftOnlyBlock } from '@/blocks/domain/helpers/isDraftOnlyBlock';
import { useDocumentBuilderPaths } from '@/common/main/routes/useDocumentBuilderPaths';
import { BLOCK_KIND_ICONS } from '@/blocks/presentation/constants/blockKindIcons';
import { BlockThumbnail } from '@/blocks/presentation/components/BlockThumbnail';
import { RwLozenge } from '@/common/presentation/components/rw';
import {
  LibraryCard,
  LibraryCardKicker,
  formatLastEdited,
  formatVersion,
  type LibraryOverflowAction
} from '@/common/presentation/components/library';

export interface BlockLibraryCardProps {
  block: Block;
  onOpen: () => void;
  onDuplicate?: () => void | Promise<void>;
  onArchive?: () => void | Promise<void>;
  onRestore?: () => void;
  isDuplicateLoading?: boolean;
  isArchiveLoading?: boolean;
  isRestoreLoading?: boolean;
}

const STATUS_TONE: Record<Block['status'], 'success' | 'information' | 'neutral'> = {
  Draft: 'information',
  Published: 'success',
  Archived: 'neutral',
  Unknown: 'neutral'
};

/**
 * One Block Library tile (Figma `bl.library.default`, node `6:3`): live preview → kind kicker
 * (icon + label) → name → status `RwLozenge` · version · "Edited <date>". Built on the Part 2
 * library kit's `LibraryCard`, whose single full-card open link means this component carries no
 * separate "Edit"/"View" affordance of its own — opening is the card's own job, and the overflow
 * (revealed on hover/focus, design ruling 2026-09-07) holds only the standalone commands that
 * don't need the record open: Duplicate on an active row (never an archived one, D-26), and never a Draft-only one (404), then Archive on any non-archived row or Restore
 * on an Archived one. Restore is dev-only — the designer prototype this Part ports from has no
 * such flow.
 */
export function BlockLibraryCard({
  block,
  onOpen,
  onDuplicate,
  onArchive,
  onRestore,
  isDuplicateLoading = false,
  isArchiveLoading = false,
  isRestoreLoading = false
}: BlockLibraryCardProps) {
  const { blockPath } = useDocumentBuilderPaths();

  const kindLabel = blockKindDisplayLabel(block);
  // An unsupported kind has no glyph of its own (its `kind` is only a type placeholder).
  const KindIcon = block.isSupportedKind === false ? undefined : BLOCK_KIND_ICONS[block.kind];
  const isArchived = block.status === 'Archived';
  // D-9: the lozenge is the block's lifecycle status. An open draft is a property of the NEXT version,
  // so it is a quiet note beside it, never a status of its own.
  const hasOpenDraft = Boolean(block.draftVersionId) && !isArchived;

  const actions: LibraryOverflowAction[] = [];
  // D-26: an archived block cannot be duplicated (the API refuses), so the UI never offers it. A
  // Draft-only block (never published) is refused too (404), so it is not offered either.
  if (onDuplicate && !isArchived && !isDraftOnlyBlock(block)) {
    actions.push({
      key: 'duplicate',
      label: 'Duplicate',
      loadingLabel: 'Duplicating...',
      icon: <ContentCopyOutlinedIcon aria-hidden />,
      onSelect: onDuplicate,
      loading: isDuplicateLoading,
      testId: 'document-builder:block-card:duplicate-action'
    });
  }
  if (isArchived && onRestore) {
    actions.push({
      key: 'restore',
      label: 'Restore',
      loadingLabel: 'Restoring...',
      icon: <UnarchiveOutlinedIcon aria-hidden />,
      onSelect: onRestore,
      loading: isRestoreLoading,
      testId: 'document-builder:block-card:restore-action'
    });
  }
  if (!isArchived && onArchive) {
    actions.push({
      key: 'archive',
      label: 'Archive',
      loadingLabel: 'Archiving...',
      icon: <ArchiveOutlinedIcon aria-hidden />,
      onSelect: onArchive,
      destructive: true,
      loading: isArchiveLoading,
      testId: 'document-builder:block-card:archive-action'
    });
  }

  return (
    <LibraryCard
      name={block.name}
      to={blockPath(block.blockId)}
      onOpen={onOpen}
      preview={<BlockThumbnail blockId={block.blockId} />}
      kicker={
        <LibraryCardKicker icon={KindIcon ? <KindIcon sx={{ fontSize: 'var(--size-16)' }} /> : undefined}>
          <span data-testid="document-builder:block-card:kind">{kindLabel}</span>
        </LibraryCardKicker>
      }
      status={
        <Box role="status" aria-label={`Block status: ${block.status}`} sx={{ display: 'inline-flex' }}>
          <RwLozenge tone={STATUS_TONE[block.status]} size="sm">
            {block.status}
          </RwLozenge>
        </Box>
      }
      version={formatVersion(block.currentVersion)}
      editedLabel={formatLastEdited(block.updatedAt)}
      extraMeta={hasOpenDraft ? ['Draft in progress'] : undefined}
      actions={actions}
      actionsLabel={`Block actions for ${block.name}`}
      actionsTestId="document-builder:block-card:actions-button"
      testId={`document-builder:block-card:${block.blockId}`}
      openTestId="document-builder:block-card:open"
    />
  );
}
